import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { SFSearchIcon } from "../icons";
import CustomOptionTemplate from "./customOptionTemplate";
import GlobalFilters from "./globalFilters";
import InputAdornment from "@mui/material/InputAdornment";
import getStyled from "../../hooks/getStyled";
import Popper from "@mui/material/Popper";

const { styled } = getStyled();

const SFAutocompleteStyled = styled(Autocomplete)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    border: "none",
  },
  "& .MuiAutocomplete-inputRoot": {
    height: "40px",
    alignContent: "center",
    border: "none",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
  },
  "& .MuiAutocomplete-root": {
    flex: 1,
  },
  "& .MuiAutocomplete-popupIndicator": {
    transform: "none",
  },
  "& .Mui-focused": {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
}));

const TextFieldStyled = styled(TextField)(({ theme }) => ({
  backgroundColor: theme.palette.grey100.main,
  borderRadius: "4px",
  input: {
    fontSize: "14px",
  },
}));

const SFTypeahead = (props) => {
  const { t } = useTranslation();
  const [inputChangeObj, setInputChangeObj] = useState({
    event: "",
    value: "",
  });
  const [typeaheadState, setTypeaheadState] = useState({
    results: [],
    key: new Date().toString(),
  });

  const source = props.source ?? [];

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (
        inputChangeObj.value.trim(" ").length > 2 &&
        inputChangeObj.event === "input"
      ) {
        changeHandler(inputChangeObj.value);
      }
    }, 400);
    if (
      inputChangeObj.value.trim(" ").length < 3 &&
      typeaheadState.results.length !== 0
    ) {
      setTypeaheadState((curState) => ({ ...curState, results: [] }));
    }
    return () => clearTimeout(delayDebounceFn);
  }, [inputChangeObj]);

  const changeHandler = async (param) => {
    if (props.fetchData) {
      let data = await props.fetchData(param);
      let results = data.map((result, index) => ({
        ...result,
        id: index,
      }));
      setTypeaheadState((curState) => ({ ...curState, results: results }));
    } else {
      let results = source
        .filter((x) => x.value.toLowerCase().includes(param.toLowerCase()))
        .map((result, index) => ({ ...result, id: index }));
      setTypeaheadState((curState) => ({ ...curState, results: results }));
    }
  };

  const MyPopper = (props) => {
    const { style, ...other } = props;
    return (
      <Popper
        style={{
          ...style,
          ...(style.width && { minWidth: style.width }),
          width: "fit-content",
        }}
        {...other}
      />
    );
  };

  return (
    <div data-testid="sf-typeahead">
      {props.globalFilters && (
        <GlobalFilters
          filters={props.globalFilters}
          onApplyFilters={props.onApplyFilters}
        />
      )}
      <SFAutocompleteStyled
        PopperComponent={MyPopper}
        forcePopupIcon={false}
        key={typeaheadState.key}
        noOptionsText={t("components.typeahead.resultsPlaceholder")}
        disablePortal={props.disablePortal}
        size={props.size}
        onChange={(event, value, reason) => {
          props.onSelect(value);
          if (props.clearOnSelect) {
            setTypeaheadState((curState) => ({
              ...curState,
              results: [],
              key: new Date().toString(),
            }));
          }
        }}
        value={props.selectedValue}
        id="sf-typeahead"
        onInputChange={(_event, newInputValue, reason) => {
          setInputChangeObj({ event: reason, value: newInputValue });
        }}
        filterOptions={(x) => x}
        options={typeaheadState.results}
        getOptionLabel={(option) => option.value}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextFieldStyled
            {...params}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SFSearchIcon color={"grey500"} />
                </InputAdornment>
              ),
            }}
            placeholder={
              props.placeholder ?? t("components.typeahead.searchPlaceholder")
            }
          />
        )}
        renderOption={CustomOptionTemplate}
      />
    </div>
  );
};

SFTypeahead.defaultProps = {
  disablePortal: false,
  clearOnSelect: true,
  size: "medium",
};

SFTypeahead.propTypes = {
  selectedValue: PropTypes.any,
  placeholder: PropTypes.string,
  fetchData: PropTypes.func,
  source: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  clearOnSelect: PropTypes.bool.isRequired,
  onApplyFilters: PropTypes.func,
  disablePortal: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  globalFilters: PropTypes.shape({
    url: PropTypes.string,
    filters: PropTypes.arrayOf(
      PropTypes.shape({
        displayName: PropTypes.string,
        name: PropTypes.string,
        values: PropTypes.arrayOf(PropTypes.string),
        selectedValues: PropTypes.arrayOf(PropTypes.string),
        color: PropTypes.string,
      }).isRequired
    ),
  }),
};

export default SFTypeahead;
